/**
 * Handles arrow drag end operation
 * @param {string} id - Arrow ID
 * @param {number} x - New x position
 * @param {number} y - New y position
 * @param {Array} arrows - Current arrows array
 * @param {Function} setArrows - Setter for arrows array
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @param {Object} currentArrow - Optional: pass current arrow object to preserve all properties (rotation, scaleX, scaleY, etc.)
 */
export const handleArrowDragEnd = (
  id,
  x,
  y,
  arrows,
  setArrows,
  saveToHistory,
  isUndoingRedoing,
  currentArrow = null // Optional: pass current arrow object to preserve all properties (rotation, scaleX, scaleY, etc.)
) => {
  // Safety check: ensure arrows is an array
  if (!Array.isArray(arrows)) {
    return;
  }

  // Check if this is a restoration by looking at the arrow data
  const arrow = currentArrow || arrows.find((a) => a.id === id);
  const isRestoration = arrow?.isRestored || arrow?.isFromHistory;

  if (isRestoration) {
    return;
  }

  // CRITICAL: Use functional update to get the current state, avoiding stale closure issues
  // This ensures we always work with the most up-to-date arrows array
  setArrows((prevArrows) => {
    // CRITICAL: Always get the latest arrow from state to ensure we have the latest points array
    // This is especially important for arrows with bends, where points can change frequently
    const latestArrowFromState = prevArrows.find((arrow) => arrow.id === id);
    
    if (!latestArrowFromState) {
      return prevArrows; // Return unchanged if arrow not found
    }

    // CRITICAL: Merge properties intelligently:
    // - Use latest state for points (to preserve bends/waypoints that may have been modified)
    // - Use currentArrow for Konva node properties (rotation, scaleX, scaleY) if provided
    // - Use new x, y from drag operation
    const updatedArrows = prevArrows.map((arrow) =>
      arrow.id === id
        ? {
            ...latestArrowFromState, // Start with latest state (includes latest points array)
            // Override with Konva node properties from currentArrow if provided
            ...(currentArrow && {
              rotation: currentArrow.rotation,
              scaleX: currentArrow.scaleX,
              scaleY: currentArrow.scaleY,
            }),
            // Always use the new position from drag
            x,
            y,
          }
        : arrow
    );
    
    // Debug logging to verify properties are preserved
    const updatedArrow = updatedArrows.find((arrow) => arrow.id === id);
    if (updatedArrow) {
    }

    // Save to history with the updated arrows
    // CRITICAL: Call saveToHistory OUTSIDE the setArrows callback to avoid React batching issues
    // Use setTimeout to ensure it's called after the state update completes
    if (saveToHistory) {
      // Check if we're currently undoing/redoing to prevent clearing redo stack
      if (isUndoingRedoing && isUndoingRedoing.current) {
        return updatedArrows;
      }

      // CRITICAL: Save drag operations immediately, not when element is unselected
      // This ensures drag operations create separate history entries that can be undone independently
      // Pass the updated arrows array as override to ensure saveToHistory gets the correct state
      // saveToHistory signature: (operationType, currentImagesOverride, currentArrowsOverride, currentDoubleSidedArrowsOverride, currentLinesOverride, ...)
      // Index 2 is currentArrowsOverride
      setTimeout(() => {
        saveToHistory("arrow_drag_end", null, updatedArrows);
      }, 0);
    }

    return updatedArrows;
  });
};

/**
 * Create a function to handle arrow drag end with bound dependencies
 * @param {Array} arrows - Current arrows array
 * @param {Function} setArrows - Function to set arrows
 * @param {Function} saveToHistory - Function to save to history
 * @param {Object} isUndoingRedoing - Ref to track undo/redo state
 * @returns {Function} Function to handle arrow drag end
 */
// Track last drag end call to prevent duplicates
let lastArrowDragEndCall = null;
const ARROW_DRAG_END_DEBOUNCE_MS = 50; // 50ms debounce window

export const createHandleArrowDragEnd = (
  arrows,
  setArrows,
  saveToHistory,
  isUndoingRedoing
) => {
  return (id, x, y, currentArrow = null) => {
    // CRITICAL: Debounce duplicate calls to prevent creating duplicate history entries
    // This handles cases where handleArrowDragEnd is called twice (e.g., from React strict mode or multiple event handlers)
    const now = Date.now();
    const callKey = `${id}-${x}-${y}`;
    
    if (
      lastArrowDragEndCall &&
      lastArrowDragEndCall.key === callKey &&
      now - lastArrowDragEndCall.timestamp < ARROW_DRAG_END_DEBOUNCE_MS
    ) {
      return; // Skip duplicate call
    }
    
    // Update last call tracking
    lastArrowDragEndCall = {
      key: callKey,
      timestamp: now,
    };
    
    return handleArrowDragEnd(
      id,
      x,
      y,
      arrows,
      setArrows,
      saveToHistory,
      isUndoingRedoing,
      currentArrow // Pass the current arrow object to preserve all properties (rotation, scaleX, scaleY, etc.)
    );
  };
};

/**
 * Normalizes loaded arrow data to ensure consistent coordinate system
 * Helper to normalize loaded arrows to invariant: points are RELATIVE to (x, y)
 * @param {Object} incomingArrow - Arrow object from loaded data
 * @returns {Object} Normalized arrow object
 */
export const normalizeLoadedArrow = (incomingArrow) => {
  // Preserve id and shape_id BEFORE spreading shape_properties/shape_info,
  // because shape_info.id is often a marker-id that would overwrite the correct UUID.
  const preservedId = incomingArrow.id;
  const preservedShapeId = incomingArrow.shape_id;

  const cleanShapeInfo = Object.fromEntries(
    Object.entries(incomingArrow.shape_info || {}).filter(
      ([, value]) => value !== null && value !== undefined,
    ),
  );
  const base = {
    ...incomingArrow,
    ...incomingArrow.shape_properties,
    ...cleanShapeInfo,
  };

  // Restore the authoritative id/shape_id so marker-ids from shape_info never win.
  // Only keep preserved values when they are real UUIDs (non-marker).
  if (preservedShapeId && !String(preservedShapeId).includes('marker')) {
    base.id = preservedShapeId;
    base.shape_id = preservedShapeId;
  } else if (preservedId && !String(preservedId).includes('marker')) {
    base.id = preservedId;
  }
  const pointsCandidates = [
    base.points,
    incomingArrow?.points,
    incomingArrow?.shape_properties?.points,
    incomingArrow?.shape_info?.points,
  ];
  const pts =
    pointsCandidates.find((p) => Array.isArray(p) && p.length >= 4) ||
    [0, 0, 0, 0];

  // IMPROVED HEURISTIC: Better detection of relative vs absolute coordinates
  // For template arrows, we know they should have relative coordinates
  // Check if this is a template arrow by looking for specific properties
  const isTemplateArrow =
    base.templateApplied ||
    base.templateName ||
    (base.shape_properties && base.shape_properties.templateApplied) ||
    (base.shape_info && base.shape_info.templateApplied);

  // Determine if incoming looks relative or absolute
  // For relative points, the FIRST point should be at (0, 0) or close to it
  // For absolute points, the points will be far from (0, 0)
  const firstPointX = pts.length >= 2 ? pts[0] : 0;
  const firstPointY = pts.length >= 2 ? pts[1] : 0;
  
  // Calculate bounds for debugging
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (let i = 0; i < pts.length; i += 2) {
    const vx = pts[i];
    const vy = pts[i + 1];
    minX = Math.min(minX, vx);
    maxX = Math.max(maxX, vx);
    minY = Math.min(minY, vy);
    maxY = Math.max(maxY, vy);
  }

  // IMPROVED: For template arrows, always treat as relative
  // For other arrows, check if FIRST point is at (0,0) or close to it
  // This is the key indicator that points are relative
  let looksRelative;
  if (isTemplateArrow) {
    looksRelative = true; // Template arrows are always relative
  } else if (base.skipOffset === true) {
    // skipOffset indicates geometry has already been normalized as local points
    looksRelative = true;
  } else {
    // Check if FIRST point is at (0,0) or close to it - this indicates relative coordinates
    // API typically returns relative points starting at [0, 0, ...]
    looksRelative = Math.abs(firstPointX) < 1 && Math.abs(firstPointY) < 1;
  }

  const hasFiniteXY =
    Number.isFinite(base.x) && Number.isFinite(base.y);

  let finalX;
  let finalY;
  let relativePoints;

  if (looksRelative) {
    // Trust supplied x,y if present; else leave at 0,0
    finalX = hasFiniteXY ? base.x : 0;
    finalY = hasFiniteXY ? base.y : 0;
    relativePoints = pts.slice();
  } else {
    // Treat as absolute points. If x,y supplied, convert using them; else derive from bounds
    if (hasFiniteXY) {
      finalX = base.x;
      finalY = base.y;
      relativePoints = pts.map((coord, i) =>
        i % 2 === 0 ? coord - finalX : coord - finalY
      );
    } else {
      finalX = minX;
      finalY = minY;
      relativePoints = pts.map((coord, i) =>
        i % 2 === 0 ? coord - finalX : coord - finalY
      );
    }
  }

  // Compute width/height from reconstructed absolute points
  let absMinX = Infinity,
    absMinY = Infinity,
    absMaxX = -Infinity,
    absMaxY = -Infinity;
  for (let i = 0; i < relativePoints.length; i += 2) {
    const ax = relativePoints[i] + finalX;
    const ay = relativePoints[i + 1] + finalY;
    absMinX = Math.min(absMinX, ax);
    absMaxX = Math.max(absMaxX, ax);
    absMinY = Math.min(absMinY, ay);
    absMaxY = Math.max(absMaxY, ay);
  }
  const width = absMaxX - absMinX;
  const height = absMaxY - absMinY;

  const result = {
    ...base,
    x: finalX,
    y: finalY,
    points: relativePoints,
    width,
    height,
    // Preserve unified format properties if they exist, or set defaults based on type
    hasStartArrow: base.hasStartArrow !== undefined 
      ? base.hasStartArrow 
      : (base.pointerAtBeginning || false),
    hasEndArrow: base.hasEndArrow !== undefined 
      ? base.hasEndArrow 
      : (base.pointerAtEnd !== undefined ? base.pointerAtEnd : (base.type === "DoubleSidedArrow" ? true : (base.type === "Arrow" ? true : false))),
  };

  if (base.skipOffset === true || isTemplateArrow) {
  }

  return result;
};

